import axios from 'axios'

import {
  assignReport as assignOperationsReport,
  createField as createOperationsField,
  createMenuItem as createOperationsMenuItem,
  createSubmenuItem as createOperationsSubmenu,
  createTemplate as createOperationsTemplate,
  deleteField as deleteOperationsField,
  deleteSubMenuItem as deleteOperationsSubmenu,
  deleteTemplate as deleteOperationsTemplate,
  getAssignedTemplatesByCompany,
  getAssignedTemplatesByCompanyAdmin,
  getAssignedTemplatesByVesselId,
  getAssignedTemplatesByVesselType,
  getFieldsForTab as getOperationsFieldsForTab,
  getReportsForTemplate as getOperationsReportsForTemplate,
  getTemplateList as getOperationsTemplateList,
  updateCustomField as updateOperationsCustomField,
  updateField as updateOperationsField,
  updateMenuItem as updateOperationsMenuItem,
  updateSubMenuItem as updateOperationsSubmenu,
} from './_requests'

import type {
  AssignedReports,
  Fields,
  Reports,
  Template,
  WarningLevel,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL

export type LibraryTemplateFilters = {
  companyGroupAdminId?: number
  companyAdminId?: number
  formType?: string
}

export type LibraryMutationContext = {
  mode?: 'master' | 'company'
  companyGroupAdminId?: number
  companyAdminId?: number
  vesselId?: number
  vesselType?: string
}

export type LibraryApiFeatures = {
  showModeToggle: boolean
  showCompanyFilters: boolean
  showVesselFilters: boolean
  showAssignReports: boolean
  useAssignments: boolean
  allowConsumptionTab: boolean
  allowEditInCompanyMode: boolean
  showFormTypeSelect: boolean
}

export type LibraryApi = {
  id: 'operations' | 'qhse'
  defaultMode: 'master' | 'company'
  features: LibraryApiFeatures
  getTemplateList: (filters?: LibraryTemplateFilters) => Promise<Template[]>
  getReportsForTemplate: (templateId: number) => Promise<Reports[]>
  getAssignedTemplatesByCompanyAdmin?: (companyAdminId: number) => Promise<AssignedReports[]>
  getAssignedTemplatesByCompany?: (companyAdminId: number, companyId: number) => Promise<AssignedReports[]>
  getAssignedTemplatesByVesselType?: (
    companyAdminId: number,
    companyId: number,
    vesselType: string
  ) => Promise<AssignedReports[]>
  getAssignedTemplatesByVesselId?: (
    companyAdminId: number,
    companyId: number,
    vesselType: string,
    vesselId: number
  ) => Promise<AssignedReports[]>
  assignReport?: (templateId: number, context: LibraryMutationContext) => Promise<unknown>
  createTemplate: (name: string, filters?: LibraryTemplateFilters) => Promise<{ id: number }>
  deleteTemplate: (templateId: number, context?: LibraryMutationContext) => Promise<unknown>
  createMenuItem: (
    templateId: number,
    reportName: string,
    active: boolean,
    context?: LibraryMutationContext
  ) => Promise<unknown>
  updateMenuItem: (
    templateId: number,
    reportId: number,
    reportName: string,
    active: boolean,
    order?: number,
    context?: LibraryMutationContext
  ) => Promise<unknown>
  deleteMenuItem?: (templateId: number, reportId: number, context?: LibraryMutationContext) => Promise<unknown>
  createSubmenuItem: (
    menuId: number,
    name: string,
    active: boolean,
    order?: number,
    context?: LibraryMutationContext
  ) => Promise<{ id: number }>
  updateSubMenuItem: (
    menuId: number,
    submenuId: number,
    name: string,
    active: boolean,
    order?: number,
    context?: LibraryMutationContext
  ) => Promise<unknown>
  deleteSubMenuItem: (menuId: number, submenuId: number, context?: LibraryMutationContext) => Promise<unknown>
  createField: (
    submenuId: number,
    label: string,
    fieldType: string,
    readOnly: boolean,
    required: boolean,
    active?: boolean,
    warningOnly?: WarningLevel,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[] | null,
    context?: LibraryMutationContext
  ) => Promise<unknown>
  updateField: (
    submenuId: number,
    fieldId: number,
    label: string,
    fieldType: string,
    required?: boolean,
    readOnly?: boolean,
    warningOnly?: WarningLevel,
    active?: boolean,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[],
    order?: number,
    context?: LibraryMutationContext
  ) => Promise<unknown>
  updateCustomField?: (
    customTemplateId: number,
    fieldId: number,
    label: string,
    fieldType: string,
    readOnly: boolean,
    required?: boolean,
    warningOnly?: WarningLevel,
    active?: boolean,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[],
    order?: number
  ) => Promise<unknown>
  deleteField: (submenuId: number, fieldId: number, context?: LibraryMutationContext) => Promise<unknown>
  getFieldsForTab?: (tabId: number) => Promise<Fields[]>
}

export const operationsLibraryApi: LibraryApi = {
  id: 'operations',
  defaultMode: 'master',
  features: {
    showModeToggle: true,
    showCompanyFilters: true,
    showVesselFilters: true,
    showAssignReports: true,
    useAssignments: true,
    allowConsumptionTab: true,
    allowEditInCompanyMode: true,
    showFormTypeSelect: false,
  },
  getTemplateList: async () => getOperationsTemplateList(),
  getReportsForTemplate: async (templateId: number) => getOperationsReportsForTemplate(templateId),
  getAssignedTemplatesByCompanyAdmin: async (companyAdminId: number) =>
    getAssignedTemplatesByCompanyAdmin(companyAdminId),
  getAssignedTemplatesByCompany: async (companyAdminId: number, companyId: number) =>
    getAssignedTemplatesByCompany(companyAdminId, companyId),
  getAssignedTemplatesByVesselType: async (companyAdminId: number, companyId: number, vesselType: string) =>
    getAssignedTemplatesByVesselType(companyAdminId, companyId, vesselType),
  getAssignedTemplatesByVesselId: async (
    companyAdminId: number,
    companyId: number,
    vesselType: string,
    vesselId: number
  ) => getAssignedTemplatesByVesselId(companyAdminId, companyId, vesselType, vesselId),
  assignReport: async (templateId: number, context: LibraryMutationContext) =>
    assignOperationsReport(templateId, context),
  createTemplate: async (name: string) => createOperationsTemplate(name),
  deleteTemplate: async (templateId: number, context?: LibraryMutationContext) =>
    deleteOperationsTemplate(templateId, context),
  createMenuItem: async (templateId: number, reportName: string, active: boolean, context?: LibraryMutationContext) =>
    createOperationsMenuItem(templateId, reportName, active),
  updateMenuItem: async (
    templateId: number,
    reportId: number,
    reportName: string,
    active: boolean,
    order?: number,
    context?: LibraryMutationContext
  ) => updateOperationsMenuItem(templateId, reportId, reportName, active, order, context),
  createSubmenuItem: async (menuId: number, name: string, active: boolean, order?: number, context?: LibraryMutationContext) =>
    createOperationsSubmenu(menuId, name, active, order),
  updateSubMenuItem: async (
    menuId: number,
    submenuId: number,
    name: string,
    active: boolean,
    order?: number,
    context?: LibraryMutationContext
  ) => updateOperationsSubmenu(menuId, submenuId, name, active, order, context),
  deleteSubMenuItem: async (menuId: number, submenuId: number, context?: LibraryMutationContext) =>
    deleteOperationsSubmenu(menuId, submenuId),
  createField: async (
    submenuId: number,
    label: string,
    fieldType: string,
    readOnly: boolean,
    required: boolean,
    active?: boolean,
    warningOnly?: WarningLevel,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[] | null,
    context?: LibraryMutationContext
  ) =>
    createOperationsField(
      submenuId,
      label,
      fieldType,
      readOnly,
      required,
      active,
      warningOnly,
      unit,
      minValue,
      maxValue,
      options
    ),
  updateField: async (
    submenuId: number,
    fieldId: number,
    label: string,
    fieldType: string,
    required?: boolean,
    readOnly?: boolean,
    warningOnly?: WarningLevel,
    active?: boolean,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[],
    order?: number,
    context?: LibraryMutationContext
  ) =>
    updateOperationsField(
      submenuId,
      fieldId,
      label,
      fieldType,
      required,
      readOnly,
      warningOnly,
      active,
      unit,
      minValue,
      maxValue,
      options,
      order,
      context
    ),
  updateCustomField: async (
    customTemplateId: number,
    fieldId: number,
    label: string,
    fieldType: string,
    readOnly: boolean,
    required?: boolean,
    warningOnly?: WarningLevel,
    active?: boolean,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[],
    order?: number
  ) =>
    updateOperationsCustomField(
      customTemplateId,
      fieldId,
      label,
      fieldType,
      readOnly,
      required,
      warningOnly,
      active,
      unit,
      minValue,
      maxValue,
      options,
      order
    ),
  deleteField: async (submenuId: number, fieldId: number, context?: LibraryMutationContext) =>
    deleteOperationsField(submenuId, fieldId),
  getFieldsForTab: async (tabId: number) => getOperationsFieldsForTab(tabId),
}

type QhseMenuResponse = {
  id: number
  name: string
  order?: number
  isActive: boolean
  templateId?: number
  submenus?: QhseSubmenuResponse[]
}

type QhseSubmenuResponse = {
  id: number
  name: string
  isActive: boolean
  menuId?: number
  fields?: QhseFieldResponse[]
}

type QhseFieldResponse = {
  id: number
  label: string
  fieldType: string
  readOnly: boolean
  isActive: boolean
  order?: number
  optionsJson?: string[]
  required: boolean
  unit?: string
  minValue?: number
  maxValue?: number
  warningOnly?: WarningLevel
  submenuId?: number
}

const mapQhseField = (field: QhseFieldResponse): Fields => ({
  id: field.id,
  label: field.label,
  fieldType: field.fieldType,
  readOnly: field.readOnly ?? false,
  isActive: field.isActive,
  order: field.order,
  optionsJson: field.optionsJson,
  required: field.required,
  unit: field.unit,
  minValue: field.minValue,
  maxValue: field.maxValue,
  warningOnly: field.warningOnly,
  submenuId: field.submenuId ?? 0,
})

const mapQhseSubmenu = (submenu: QhseSubmenuResponse): Reports['submenus'][number] => ({
  id: submenu.id,
  name: submenu.name,
  isActive: submenu.isActive,
  fields: (submenu.fields ?? []).map(mapQhseField),
  menuId: submenu.menuId ?? 0,
})

const mapQhseMenuToReport = (menu: QhseMenuResponse): Reports => ({
  id: menu.id,
  name: menu.name,
  order: menu.order ?? 0,
  templateId: menu.templateId ?? 0,
  isActive: menu.isActive,
  submenus: (menu.submenus ?? []).map(mapQhseSubmenu),
})

const qhseCreateTemplatePayload = (
  name: string,
  filters?: LibraryTemplateFilters
) => ({
  name,
  description: '',
  formType: filters?.formType ?? 'FORM',
  isActive: true,
  companyGroupAdminId: filters?.companyGroupAdminId,
  companyAdminId: filters?.companyAdminId,
})

export const qhseLibraryApi: LibraryApi = {
  id: 'qhse',
  defaultMode: 'company',
  features: {
    showModeToggle: false,
    showCompanyFilters: true,
    showVesselFilters: false,
    showAssignReports: false,
    useAssignments: false,
    allowConsumptionTab: false,
    allowEditInCompanyMode: true,
    showFormTypeSelect: true,
  },
  getTemplateList: async (filters?: LibraryTemplateFilters) => {
    const params = {
      companyGroupId: filters?.companyGroupAdminId,
      companyAdminId: filters?.companyAdminId,
      formType: filters?.formType,
    }
    const response = await axios.get(`${API_URL}/qhse/forms/templates`, {
      params,
    })
    return response.data
  },
  getReportsForTemplate: async (templateId: number) => {
    const response = await axios.get(`${API_URL}/qhse/forms/templates/${templateId}/menus`)
    const menus: QhseMenuResponse[] = response.data ?? []
    return menus.map(mapQhseMenuToReport)
  },
  createTemplate: async (name: string, filters?: LibraryTemplateFilters) => {
    if (!filters?.companyGroupAdminId) {
      throw new Error('companyGroupAdminId is required to create QHSE templates')
    }
    const response = await axios.post(
      `${API_URL}/qhse/forms/templates/create`,
      qhseCreateTemplatePayload(name, filters)
    )
    return response.data
  },
  deleteTemplate: async (templateId: number) => {
    const response = await axios.delete(`${API_URL}/qhse/forms/templates/delete/${templateId}`)
    return response.data
  },
  createMenuItem: async (templateId: number, reportName: string, active: boolean) => {
    const response = await axios.post(`${API_URL}/qhse/forms/templates/${templateId}/menus/create`, {
      name: reportName,
      isActive: active,
    })
    return response.data
  },
  updateMenuItem: async (
    templateId: number,
    reportId: number,
    reportName: string,
    active: boolean,
    order?: number
  ) => {
    const response = await axios.put(`${API_URL}/qhse/forms/templates/${templateId}/menus/update/${reportId}`, {
      name: reportName,
      isActive: active,
      order,
    })
    return response.data
  },
  createSubmenuItem: async (menuId: number, name: string, active: boolean, order?: number) => {
    const response = await axios.post(`${API_URL}/qhse/forms/menus/${menuId}/submenus/create`, {
      name,
      isActive: active,
      order,
    })
    return response.data
  },
  updateSubMenuItem: async (
    menuId: number,
    submenuId: number,
    name: string,
    active: boolean,
    order?: number
  ) => {
    const response = await axios.put(`${API_URL}/qhse/forms/menus/${menuId}/submenus/update/${submenuId}`, {
      name,
      isActive: active,
      order,
    })
    return response.data
  },
  deleteSubMenuItem: async (menuId: number, submenuId: number) => {
    const response = await axios.delete(`${API_URL}/qhse/forms/menus/${menuId}/submenus/delete/${submenuId}`)
    return response.data
  },
  createField: async (
    submenuId: number,
    label: string,
    fieldType: string,
    readOnly: boolean,
    required: boolean,
    active?: boolean,
    warningOnly?: WarningLevel,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[] | null
  ) => {
    const response = await axios.post(`${API_URL}/qhse/forms/submenus/${submenuId}/fields/create`, {
      label,
      fieldType,
      readOnly,
      required,
      isActive: active,
      warningOnly,
      unit,
      minValue,
      maxValue,
      optionsJson: options ?? undefined,
    })
    return response.data
  },
  updateField: async (
    submenuId: number,
    fieldId: number,
    label: string,
    fieldType: string,
    required?: boolean,
    readOnly?: boolean,
    warningOnly?: WarningLevel,
    active?: boolean,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[],
    order?: number
  ) => {
    const response = await axios.put(`${API_URL}/qhse/forms/submenus/${submenuId}/fields/update/${fieldId}`, {
      label,
      fieldType,
      required,
      readOnly,
      warningOnly,
      isActive: active,
      unit,
      minValue,
      maxValue,
      optionsJson: options,
      order,
    })
    return response.data
  },
  deleteField: async (submenuId: number, fieldId: number) => {
    const response = await axios.delete(`${API_URL}/qhse/forms/submenus/${submenuId}/fields/delete/${fieldId}`)
    return response.data
  },
  getFieldsForTab: async (tabId: number) => {
    const response = await axios.get(`${API_URL}/qhse/forms/submenus/${tabId}/fields`)
    const fields: QhseFieldResponse[] = response.data ?? []
    return fields.map(mapQhseField)
  },
}
